import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from './design-tokens';
import {StatusPill} from './StatusPill';
import {ActionPhase} from './actionCardTypes';
import {fieldByKey, type ReportTaxonomy} from '../ai-report-builder/contracts/report-taxonomy.types';
import type {Condition, FieldNode, ReportDefinitionV1} from '../ai-report-builder/contracts/report-definition.types';
import {formatConditionSummary, SummaryLabels} from '../ai-report-builder/conditions/formatConditionSummary';
import {GROUPING_TOKEN_LABEL_KEY} from '../ai-report-builder/GroupByControl';
import {findRelativeDatePreset} from '../ai-report-builder/relativeDatePresets';
import {groupingCellPaths, type GroupingToken} from '../ai-report-builder/ReportTable/useClientGrouping';
import {grainRootOf} from '../ai-report-builder/data/definitionFields';
import {DEFAULT_FIELD_LABELS} from '../ai-report-builder/ReportTable/fieldLabels';

const {font, color} = tokensForThemeProvider.sky.sys;
const button = tokensForThemeProvider.sky.comp.button;

/** Confirm card for an agent-proposed report build. The agent no longer opens the builder directly;
 * it proposes the report here (mirroring the write-action HITL cards) and the user confirms before
 * anything opens. On confirm the card settles to a Done state exposing a View action that opens the
 * builder. Chrome intentionally mirrors `ActionCard` so report proposals read like other HITL
 * actions. */

const Card = styled.div`
	border: 1px solid ${D.assistantBubbleBorder};
	border-radius: 8px;
	overflow: hidden;
	margin-top: 8px;
`;
const Body = styled.div`
	background: ${D.assistantBubble};
	padding: 16px;
`;
const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	gap: 8px;
`;
const HeaderTitle = styled.div`
	${font.heading['4']}
	color: ${color.text.content.main};
`;
const ReportBox = styled.div`
	border: 1px solid ${D.assistantBubbleBorder};
	border-left: 3px solid ${D.v2.accent};
	border-radius: 4px;
	padding: 12px;
	background: ${D.bg};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
`;
const ReportBoxContent = styled.div`
	min-width: 0;
`;
const ReportName = styled.div`
	${font.subheading['2']}
	color: ${color.text.content.main};
`;
/** Confirmed-state open affordance: a plain-text blue link inside the report box (not a filled
 * footer button), matching the other settled HITL cards. A real <button> so it stays keyboard-
 * and screen-reader-accessible despite the link styling. */
const ViewLink = styled.button`
	flex-shrink: 0;
	border: none;
	background: none;
	padding: 0;
	${button.font.standard}
	color: ${button.color.text.lowemphasis.default};
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
`;
const ReportSummary = styled.p`
	margin: 4px 0 0;
	${font.body['2']}
	color: ${color.text.content.main};
`;
/** Agent-emitted warnings (out-of-scope terms it dropped, filter values it couldn't resolve),
 * listed under the report box so the user sees what was excluded/unmatched before confirming. */
const FlagList = styled.ul`
	list-style: none;
	margin: 8px 0 0;
	padding: 0;
`;
const FlagItem = styled.li`
	${font.body['2']}
	color: ${D.v2.textSecondary};
	margin-top: 4px;
`;
const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 16px;
	align-items: center;
	padding: 12px 16px;
	background: ${D.bg};
	border-top: 1px solid ${D.assistantBubbleBorder};
`;
const Cancelled = styled.p`
	margin: 0 0 12px;
	font-size: 14px;
	line-height: 20px;
	color: ${D.v2.textSecondary};
`;

export type ReportIntentStatus = 'pending' | 'confirmed' | 'cancelled';

export interface ReportIntentCardProps {
	payload: Record<string, unknown>;
	status: ReportIntentStatus;
	taxonomy?: ReportTaxonomy;
	onConfirm: () => void;
	onCancel: () => void;
	onView: () => void;
}

// Only the two narrowing scopes are named on the card. "all_dates" is the default a report has
// when the agent sent no scope at all, so rendering it would put a line on every card that says
// nothing; the narrowing scopes are the ones that change which tasks come back.
const DATE_SCOPE_LABEL_KEY: Record<string, string> = {
	start_date: 'ai_report_builder.date_scope.start_date',
	deadline: 'ai_report_builder.date_scope.deadline',
};

/** Wire prefix of the grouping token that names one company's own project custom field
 * (the report definition contract). The label after it IS the field's display name, verbatim. */
const CUSTOM_FIELD_TOKEN_PREFIX = 'customFields.';

/** A grouping token's display name, resolved the way `GroupByControl`'s own `TokenLabel` resolves
 * it: the translated name for one of the task grain's eight, else the catalog's label for the cell
 * the token groups by, else the static English name of that task field, else a custom field's own
 * label, else the raw token.
 *
 * Nothing is skipped. Filtering the tokens through `GROUPING_TOKEN_LABEL_KEY` — which holds the
 * TASK grain's eight and nothing else — dropped every project-status token (`stage`, `rag`,
 * `owner`, `budgetType`, `labels`, `priority`) and every `customFields.<Label>` one, so a
 * project-status report proposal rendered no grouping line at all and read as ungrouped. A token
 * this card cannot name is still a level the report is grouped by, and the user is confirming the
 * proposal from what this card says. */
function groupingTokenLabel(
	token: GroupingToken,
	cellPaths: Record<string, string>,
	taxonomy: ReportTaxonomy | undefined,
	translate: (id: string) => string
): string {
	const messageId = GROUPING_TOKEN_LABEL_KEY[token];
	if (messageId) {
		return translate(messageId);
	}
	const cellPath = cellPaths[token];
	const label = taxonomy ? fieldByKey(taxonomy, cellPath ?? '')?.label : undefined;
	if (label) {
		return label;
	}
	// A task token without a translated name still has a static English one, so the card does not
	// print the wire spelling while the catalog is still loading.
	const staticLabel = cellPath ? DEFAULT_FIELD_LABELS[cellPath] : undefined;
	if (staticLabel) {
		return staticLabel;
	}
	// A custom field's token carries its own display name (the contract makes the label the
	// identity), so it can still be named when the catalog has not resolved — which is the common
	// case here, since the taxonomy is fetched alongside the card rather than before it.
	if (token.startsWith(CUSTOM_FIELD_TOKEN_PREFIX)) {
		return token.slice(CUSTOM_FIELD_TOKEN_PREFIX.length);
	}
	return token;
}

/** Parses a `YYYY-MM-DD` definition date as a LOCAL calendar date. `new Date('2026-10-01')` parses
 * as UTC midnight, which renders as September 30 anywhere west of Greenwich — a card that reads a
 * day earlier than the range the builder applies. */
function parseDefinitionDate(value: unknown): Date | null {
	if (typeof value !== 'string') return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return null;
	return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function ReportIntentCard({payload, status, taxonomy, onConfirm, onCancel, onView}: ReportIntentCardProps): JSX.Element {
	const {formatMessage, formatDate} = useIntl();

	// The backend always sets this to the grain's display name ('Task report', 'Project Status', …)
	// — see report_type_display_name in report_builder/grain.py. Neither convention ends in "report"
	// consistently, so normalize here rather than hardcoding one grain's wording in the card copy.
	const reportTypeRaw =
		typeof payload.reportType === 'string' && payload.reportType.trim() ? payload.reportType : 'task report';
	const reportTypeLabel = /report$/i.test(reportTypeRaw) ? reportTypeRaw : `${reportTypeRaw} report`;

	const name =
		typeof payload.suggestedName === 'string' && payload.suggestedName.trim()
			? payload.suggestedName
			: formatMessage(
					{id: 'forecast_agent.report_card.default_name', defaultMessage: 'New {reportType}'},
					{reportType: reportTypeLabel}
			  );
	// One-line description shown beneath the name. Preferred source: the backend's `summaries`
	// (objects `{conditionId, field, text}` from report_builder nodes) — these are the agent's own
	// human-written summary and read correctly even for id-backed or enum-backed fields. Falls back
	// to a taxonomy-built summary (field labels/operator labels/value formatting over the report's
	// FILTER conditions) only when the agent sent no usable summaries; that taxonomy path has no
	// access to a label resolver, so it renders raw ids/enum values verbatim and is a lesser
	// fallback, not the preferred source. A report with neither (e.g. "all tasks grouped by
	// project", no filter) falls back further to the original natural-language prompt so the card
	// always shows what the report is. A bare string element in `summaries` is tolerated for
	// forward-compatibility.
	const labels: SummaryLabels = {
		or: formatMessage({id: 'ai_report_builder.conditions.summary_or', defaultMessage: 'or'}),
		and: formatMessage({id: 'ai_report_builder.conditions.summary_and', defaultMessage: 'and'}),
		selected: n =>
			formatMessage(
				{id: 'ai_report_builder.conditions.summary_selected', defaultMessage: '{count} selected'},
				{count: n}
			),
		yes: formatMessage({id: 'common.yes', defaultMessage: 'Yes'}),
		no: formatMessage({id: 'common.no', defaultMessage: 'No'}),
	};

	const definition = payload.definition as {filter?: {filterConditions?: Condition[]}} | undefined;
	const filterConditions = definition?.filter?.filterConditions;
	const summaryFromConditions = Array.isArray(payload.summaries)
		? payload.summaries
				.map(s => {
					if (typeof s === 'string') return s;
					if (s && typeof s === 'object' && typeof (s as {text?: unknown}).text === 'string') {
						return (s as {text: string}).text;
					}
					return '';
				})
				.filter(part => part.trim().length > 0)
				.join(' ')
		: '';
	const summaryFromTaxonomy =
		taxonomy && Array.isArray(filterConditions) && filterConditions.length > 0
			? (() => {
					try {
						return filterConditions.map(c => formatConditionSummary(c, taxonomy, {labels})).join('; ');
					} catch {
						return '';
					}
			  })()
			: '';
	const promptText = typeof payload.prompt === 'string' ? payload.prompt.trim() : '';
	const summaryText = summaryFromConditions || summaryFromTaxonomy || promptText;

	// Date range line. A preset is shown by NAME and by the dates it currently resolves to: the name
	// is what gets saved and what makes the report roll forward, the dates are what this report
	// covers if confirmed today, and the user needs both to judge the proposal before confirming.
	// Resolution goes through the builder's own findRelativeDatePreset rather than a second copy of
	// the quarter/week arithmetic, so the card can never disagree with the range the builder applies.
	const dateDefinition = payload.definition as
		| {datePreset?: unknown; startDate?: unknown; endDate?: unknown; dateScope?: unknown}
		| undefined;
	const preset = findRelativeDatePreset(
		typeof dateDefinition?.datePreset === 'string' ? dateDefinition.datePreset : undefined
	);
	const resolvedRange = preset ? preset.resolve(new Date()) : null;
	const rangeStart = parseDefinitionDate(resolvedRange ? resolvedRange.startDate : dateDefinition?.startDate);
	const rangeEnd = parseDefinitionDate(resolvedRange ? resolvedRange.endDate : dateDefinition?.endDate);
	const scopeLabelKey =
		typeof dateDefinition?.dateScope === 'string' ? DATE_SCOPE_LABEL_KEY[dateDefinition.dateScope] : undefined;
	const rangeText =
		rangeStart && rangeEnd
			? preset
				? formatMessage(
						{
							id: 'ai_report_builder.report_card.date_range_preset',
							defaultMessage: '{preset} ({start} - {end})',
						},
						{
							preset: formatMessage({id: preset.labelId}),
							start: formatDate(rangeStart),
							end: formatDate(rangeEnd),
						}
				  )
				: formatMessage(
						{id: 'ai_report_builder.report_card.date_range', defaultMessage: '{start} - {end}'},
						{start: formatDate(rangeStart), end: formatDate(rangeEnd)}
				  )
			: '';
	const dateRangeText = [rangeText, scopeLabelKey ? formatMessage({id: scopeLabelKey}) : '']
		.filter(part => part.length > 0)
		.join(' \u00b7 ');

	// Grouping line, rendered from the definition directly (the agent's `summaries` only ever describe
	// filter conditions, never grouping). Shown alongside the condition summary so a refinement that
	// adds a filter never makes it look like grouping was dropped. Every token is named — see
	// `groupingTokenLabel` for why none may be skipped.
	const groupingDefinition = payload.definition as {grouping?: unknown; fields?: FieldNode[]} | undefined;
	const groupingTokens = groupingDefinition?.grouping;
	const groupingCellPathByToken = groupingCellPaths(
		grainRootOf({fields: (groupingDefinition?.fields ?? []) as ReportDefinitionV1['fields']}),
		taxonomy
	);
	const groupLabels = Array.isArray(groupingTokens)
		? groupingTokens
				.filter((t): t is GroupingToken => typeof t === 'string' && t !== '')
				.map(token => groupingTokenLabel(token, groupingCellPathByToken, taxonomy, id => formatMessage({id})))
		: [];
	const groupingText = groupLabels.length
		? formatMessage(
				{id: 'ai_report_builder.report_card.grouped_by', defaultMessage: 'Grouped by {groups}'},
				{groups: groupLabels.join(', ')}
		  )
		: '';

	// Agent warnings: out-of-scope terms it removed and filter values it couldn't resolve (each
	// carries a human `message`; unresolved ones may also carry ranked `candidates`).
	const flags = Array.isArray(payload.flags) ? payload.flags : [];

	if (status === 'cancelled') {
		return (
			<Cancelled data-testid="report-intent-cancelled">
				{formatMessage({id: 'forecast_agent.action_card.cancelled', defaultMessage: 'Cancelled.'})}
			</Cancelled>
		);
	}

	const confirmed = status === 'confirmed';

	return (
		<Card data-cy="report-intent-card">
			<Body>
				<Header>
					<HeaderTitle>
						{formatMessage(
							{id: 'forecast_agent.report_card.title', defaultMessage: 'Build new {reportType}'},
							{reportType: reportTypeLabel}
						)}
					</HeaderTitle>
					{confirmed && <StatusPill phase={ActionPhase.Done} />}
				</Header>
				<ReportBox>
					<ReportBoxContent>
						<ReportName>{name}</ReportName>
						{dateRangeText ? (
							<ReportSummary data-cy="report-intent-date-range">{dateRangeText}</ReportSummary>
						) : null}
						{groupingText ? <ReportSummary data-cy="report-intent-grouping">{groupingText}</ReportSummary> : null}
						{summaryText ? <ReportSummary>{summaryText}</ReportSummary> : null}
					</ReportBoxContent>
					{confirmed && (
						<ViewLink type="button" onClick={onView} data-cy="report-intent-view">
							{formatMessage({id: 'forecast_agent.report_card.view', defaultMessage: 'View'})}
						</ViewLink>
					)}
				</ReportBox>
				{flags.length > 0 && (
					<FlagList data-testid="report-intent-flags" data-cy="report-intent-flags">
						{flags.map((flag, index) => {
							const record = (flag && typeof flag === 'object' ? flag : {}) as {
								message?: unknown;
								term?: unknown;
								candidates?: unknown;
							};
							const message =
								typeof record.message === 'string' && record.message.trim()
									? record.message.trim()
									: typeof record.term === 'string'
									? record.term.trim()
									: '';
							if (!message) {
								return null;
							}
							const candidates = Array.isArray(record.candidates)
								? record.candidates.filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
								: [];
							const suffix =
								candidates.length > 0
									? ' ' +
									  formatMessage(
											{
												id: 'ai_report_builder.report_card.did_you_mean',
												defaultMessage: 'Did you mean: {candidates}?',
											},
											{candidates: candidates.join(', ')}
									  )
									: '';
							return (
								<FlagItem key={index}>
									{message}
									{suffix}
								</FlagItem>
							);
						})}
					</FlagList>
				)}
			</Body>
			{!confirmed && (
				<Footer>
					<Button emphasis={EMPHASIS.LOW} onPress={onCancel}>
						{formatMessage({id: 'forecast_agent.action_card.cancel', defaultMessage: 'Cancel'})}
					</Button>
					<Button emphasis={EMPHASIS.HIGH} icon={'checkCircle'} onPress={onConfirm} data-cy="report-intent-confirm">
						{formatMessage({id: 'forecast_agent.action_card.confirm', defaultMessage: 'Confirm Action'})}
					</Button>
				</Footer>
			)}
		</Card>
	);
}
